import React, { useMemo, useRef } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Button from '@/core/components/Button';
import { ContainerWithMaxWidth } from '@/core/components/ContainerWithMaxWidth';
import { useIsLg } from '@/core/hooks/useMediaQuery';
import { useValidatedTags } from '@/core/hooks/useValidatedTags';
import Store from '@/core/lib/new-architecture/store';
import { useThematique } from '@/core/lib/new-architecture/store/thematique.store';
import { useRouterContext } from '@/core/lib/router/router.context';
import Routes from '@/core/lib/router/routes';
import { QuerySortEnum } from '@/core/types/donation';
import { SliderNavigation } from '@/next/components/SliderNavigation';

export type UniverProps = {
  style: { backgroundColor: `#${string}`; textColor?: `#${string}` };
  overTitle: string;
  title: string;
  description: string;
  landscapeImg: string;
  portraitImg: string;
  universTag: string;

  button: { title: string; url: string };
  items: (Omit<ItemProps, 'tags'> & { tag: string })[];
};

export const Univers: React.FC<UniverProps> = ({ overTitle, title, description, landscapeImg, portraitImg, items, style, universTag, button }) => {
  const { data: current } = Store.current.useCurrent();

  const queryParams = current?.getCityOrSubResult() ? { sort: QuerySortEnum.DISTANCE, loc: current?.getCityOrSubResult()?.alias, distance: 300 } : {};

  const isLg = useIsLg();
  const { data: universData } = useThematique(universTag, queryParams);
  const { push } = useRouterContext();

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  // Validate all item tags
  const allItemTags = useMemo(() => items.map(item => item.tag), [items]);
  const { data: validatedData, isLoading } = useValidatedTags(allItemTags, queryParams);

  // Filter items to only include those with valid tags
  // Show all items during loading to prevent hydration mismatch
  const validItems = useMemo(() => {
    // During loading or if no data yet, show all items to prevent hydration mismatch
    if (isLoading || !validatedData) {
      return items;
    }
    // Once loaded, filter by validated tags
    return items.filter(item => validatedData.validTags.includes(item.tag));
  }, [items, validatedData, isLoading]);

  return (
    <section className="space-y-3 lg:space-y-6">
      <ContainerWithMaxWidth>
        <p className="text-heading text-balance font-semibold">{overTitle}</p>
      </ContainerWithMaxWidth>
      <ContainerWithMaxWidth className="max-md:p-0">
        <div className="overflow-hidden md:rounded-3 lg:grid lg:grid-cols-[1fr_2fr] lg:rounded-6" style={{ backgroundColor: style.backgroundColor, color: style.textColor }}>
          <div className="grid items-center overflow-hidden max-lg:h-[276px]">
            <div className="size-full bg-cover bg-center lg:hidden" style={{ backgroundImage: `url(${landscapeImg})` }}></div>
            <div className="size-full bg-cover bg-center max-lg:hidden" style={{ backgroundImage: `url(${portraitImg})` }}></div>
          </div>
          <div className="space-y-6 p-4 lg:space-y-10 lg:overflow-hidden lg:p-8">
            <div className="space-y-3 lg:space-y-5">
              <h2 className="text-balance text-[42px] font-medium leading-[42px] lg:text-64 lg:leading-[65px]">{title}</h2>
              <p className="text-body-primary">{description}</p>
            </div>
            <div>
              <Button isInline={isLg} onClick={() => universData && push(universData.getRoute())}>
                {button.title}
              </Button>
            </div>
            <div className="relative min-h-[197px] lg:min-h-[261px]">
              <Swiper modules={[Navigation]} spaceBetween={20} className="univers-swiper" navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }} slidesPerView={'auto'}>
                {validItems.map(({ tag, ...itemProps }) => (
                  <SwiperSlide key={tag} className="!w-[188px] lg:!w-[248px]">
                    <Item {...itemProps} tag={tag} />
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="mt-4 hidden justify-end gap-4 md:flex">
                <SliderNavigation buttonRef={prevRef} type={'prev'} />
                <SliderNavigation buttonRef={nextRef} type={'next'} />
              </div>
            </div>
          </div>
        </div>
      </ContainerWithMaxWidth>
    </section>
  );
};

type ItemProps = { img: string; label: string; tag: string };
const Item: React.FC<ItemProps> = ({ label, img, tag }) => {
  const { data: current } = Store.current.useCurrent();

  const queryParams = current?.getCityOrSubResult() ? { sort: QuerySortEnum.DISTANCE, loc: current?.getCityOrSubResult()?.alias, distance: 300 } : {};

  const { data } = useThematique(tag, queryParams);
  const { Link } = useRouterContext();

  // Fallback to CatalogueRoute if data is not available yet
  const route = data?.getRoute() || new Routes.CatalogueRoute({});

  return (
    <Link href={route} className="text-body-primary group grid min-w-45 grid-rows-[auto_1fr] justify-center rounded-2 bg-[white] text-center font-semibold text-content-primary">
      <span className="flex w-45 items-center justify-center overflow-hidden lg:h-[213px] lg:w-full">
        <img src={img} alt={label} className="transition-all group-hover:scale-110" />
      </span>
      <span className="block p-3">{label}</span>
    </Link>
  );
};
